import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { getListingBySlug } from '../api';
import { assetUrl } from '../utils';

const ListingDetail = ({ listingType, backTo }) => {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await getListingBySlug(slug, listingType);
        if (active) setListing(data);
      } catch (err) {
        if (active) setError(err.response?.data?.message || 'Listing not found');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [slug, listingType]);

  const renderPairs = (pairs) => (
    <div className="grid gap-[0.85rem] md:grid-cols-2">
      {pairs.map(([label, value]) =>
        value ? (
          <div key={label} className="rounded-lg border border-[#262626] bg-[#1A1A1A] p-[1rem]">
            <p className="text-[12px] uppercase tracking-[0.12em] text-[#999]">{label}</p>
            <p className="mt-[0.3rem] text-[16px] font-semibold text-white">{value}</p>
          </div>
        ) : null
      )}
    </div>
  );

  if (loading) {
    return <div className="px-[1.25rem] py-[4rem] text-center text-white">Loading listing...</div>;
  }

  if (error || !listing) {
    return (
      <div className="px-[1.25rem] py-[4rem] text-center">
        <p className="text-[24px] font-bold text-white">{error || 'Listing not found'}</p>
        <Link to={backTo} className="mt-[1rem] inline-block rounded-lg bg-[#EEC24B] px-[1rem] py-[0.85rem] text-[15px] font-semibold text-black">
          Back to listings
        </Link>
      </div>
    );
  }

  const rentDetails = listing.rentDetails || {};
  const propertyDetails = listing.propertyDetails || {};
  const amenities = propertyDetails.amenities || {};

  return (
    <div className="w-full bg-black">
      <section className="border-b border-[#262626] bg-[#1A1A1A] px-[1.25rem] py-[2rem] md:px-[4rem]">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-[1rem]">
          <Link to={backTo} className="text-[14px] font-semibold text-[#EEC24B]">
            Back to listings
          </Link>
          <div className="grid gap-[1.5rem] lg:grid-cols-[1.2fr_0.8fr]">
            <div className="overflow-hidden rounded-[18px] border border-[#262626] bg-[#111]">
              <img src={assetUrl(listing.mainImage)} alt={listing.title} className="h-[360px] w-full object-cover" />
            </div>
            <div className="flex flex-col gap-[1rem] rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
              <div className="flex items-center gap-[0.5rem] text-[#EEC24B]">
                <FaMapMarkerAlt size={14} />
                <span className="text-[13px] font-semibold uppercase tracking-[0.16em]">
                  {listingType === 'rent' ? 'For Rent / Lease' : 'Property Listing'}
                </span>
              </div>
              <h1 className="text-[34px] font-bold leading-tight text-white">{listing.title}</h1>
              <p className="text-[16px] font-light leading-relaxed text-[#BDBDBD]">{listing.summary || listing.description}</p>
              {listing.externalUrl ? (
                <a
                  href={listing.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-fit items-center rounded-lg bg-[#EEC24B] px-[1rem] py-[0.85rem] text-[15px] font-semibold text-black"
                >
                  Open source listing
                </a>
              ) : null}
              {listing.sampleWebsite ? (
                <a href={listing.sampleWebsite} target="_blank" rel="noreferrer" className="text-[15px] font-semibold text-white">
                  Sample website
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="px-[1.25rem] py-[3rem] md:px-[4rem]">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-[1.5rem]">
          <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#EEC24B]">Overview</p>
            <p className="mt-[0.5rem] text-[17px] leading-relaxed text-white">{listing.description || listing.summary}</p>
          </div>

          {listingType === 'rent' ? (
            <>
              <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                <h2 className="text-[24px] font-bold text-white">Property Facts</h2>
                <div className="mt-[1rem] grid gap-[0.85rem] md:grid-cols-2">
                  {[
                    ['Building Size', rentDetails.buildingSize],
                    ['Property Type', rentDetails.propertyType],
                    ['Property Subtype', rentDetails.propertySubtype],
                    ['Year Built', rentDetails.yearBuilt],
                    ['Lot Size', rentDetails.lotSize],
                  ].map(([label, value]) =>
                    value ? (
                      <div key={label} className="rounded-lg border border-[#262626] bg-[#1A1A1A] p-[1rem]">
                        <p className="text-[12px] uppercase tracking-[0.12em] text-[#999]">{label}</p>
                        <p className="mt-[0.25rem] text-[16px] font-semibold text-white">{value}</p>
                      </div>
                    ) : null
                  )}
                </div>
              </div>

              {rentDetails.aboutProperty ? (
                <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                  <h2 className="text-[24px] font-bold text-white">About the Property</h2>
                  <p className="mt-[0.75rem] text-[16px] leading-relaxed text-[#BDBDBD]">{rentDetails.aboutProperty}</p>
                </div>
              ) : null}

              {rentDetails.propertyOverview ? (
                <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                  <h2 className="text-[24px] font-bold text-white">Property Overview</h2>
                  <p className="mt-[0.75rem] text-[16px] leading-relaxed text-[#BDBDBD]">{rentDetails.propertyOverview}</p>
                </div>
              ) : null}

              {rentDetails.locationOverview ? (
                <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                  <h2 className="text-[24px] font-bold text-white">Location Overview</h2>
                  <p className="mt-[0.75rem] text-[16px] leading-relaxed text-[#BDBDBD]">{rentDetails.locationOverview}</p>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                <h2 className="text-[24px] font-bold text-white">Property Details</h2>
                {renderPairs([
                  ['MLS #', propertyDetails.mlsNumber],
                  ['MLS Type', propertyDetails.mlsType],
                  ['Property Type', propertyDetails.propertyType],
                  ['Original Price', propertyDetails.originalPrice],
                  ['Sold Price', propertyDetails.soldPrice],
                  ['Bedrooms', propertyDetails.bedrooms],
                  ['Bathrooms', propertyDetails.bathrooms],
                  ['Size', propertyDetails.size],
                  ['Year Built', propertyDetails.yearBuilt],
                  ['Lot size', propertyDetails.lotSize],
                  ['HOA Fees', propertyDetails.hoaFees],
                  ['County', propertyDetails.county],
                  ['Represented', propertyDetails.represented],
                ])}
              </div>

              <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
                <h2 className="text-[24px] font-bold text-white">Amenities</h2>
                <div className="mt-[1rem] grid gap-[0.85rem] md:grid-cols-2">
                  {Object.entries(amenities).map(([key, value]) =>
                    value ? (
                      <div key={key} className="rounded-lg border border-[#262626] bg-[#1A1A1A] p-[1rem]">
                        <p className="text-[12px] uppercase tracking-[0.12em] text-[#999]">{key.replace(/([A-Z])/g, ' $1')}</p>
                        <p className="mt-[0.25rem] text-[15px] font-semibold text-white">{String(value)}</p>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            </>
          )}

          {listing.galleryImages?.length ? (
            <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]">
              <h2 className="text-[24px] font-bold text-white">Gallery</h2>
              <div className="mt-[1rem] grid gap-[0.85rem] sm:grid-cols-2 xl:grid-cols-3">
                {listing.galleryImages.map((image, index) => (
                  <img key={`${image}-${index}`} src={assetUrl(image)} alt={`${listing.title} ${index + 1}`} className="h-[220px] w-full rounded-lg object-cover" />
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-[0.75rem]">
            <Link to={backTo} className="rounded-lg border border-[#262626] px-[1rem] py-[0.85rem] text-[15px] font-semibold text-white">
              Back to listings
            </Link>
            <Link to="/contact" className="rounded-lg bg-[#EEC24B] px-[1rem] py-[0.85rem] text-[15px] font-semibold text-black">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListingDetail;
